/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { Provider, createContext, useContext } from "react";

import { CreateCallbacksResult } from "./CreateCallbacksResult";

/**
 * Creates the React context and hooks to provide and consume experience callbacks.
 * @returns The React context and hooks to provide and consume experience callbacks.
 */
export const createCallbacks = <TCallbacks>(): CreateCallbacksResult<TCallbacks> => {
    const CallbacksContext = createContext<TCallbacks | undefined>(undefined);

    const useCallbacks = (): TCallbacks => {
        const context = useContext(CallbacksContext);

        if (!context) {
            throw new Error("No callbacks found: please use <CallbacksProvider value={callbacks}> to pass required callbacks from your entrypoint package.");
        }

        return context;
    };

    const CallbacksProvider: Provider<TCallbacks> = CallbacksContext.Provider;

    return {
        CallbacksProvider,
        useCallbacks
    };
};
